import React, { useCallback, useContext, useEffect, useState } from 'react';
import {
  Button,
  MenuItem,
  ListItemText,
  OutlinedInput,
  Checkbox,
  Select,
  FormControl,
  TextField,
  Typography,
  Grid,
  Tooltip,
  CircularProgress,
} from '@mui/material';
import { createPortal } from 'react-dom';
import styles from './styles.module.css';
import DataTable from 'components/utils/DataTable';
import DateTimeHelpers from 'helpers/DateTimeHelpers';
import ImportExportIcon from '@mui/icons-material/ImportExport';
import SyncIcon from '@mui/icons-material/Sync';
import { saveAs } from 'file-saver';
import 'date-fns';
import Info from 'components/utils/Alert/Info';
import withConsoleBase from 'components/utils/ConsoleBase/withConsoleBase';
import { ROUTES, SelectFilterStyle, PRINT_TYPES } from 'const';
import { useReportPrint } from 'hooks/useReportPrint';
import PrintDocument from 'components/utils/PrintDocument/index';
import { useHistory } from 'react-router-dom';
import { ShopContext } from 'Context/ShopContext';
import CsvHelper from 'helpers/CsvHelper';
import { AlertMessagesContext } from 'react-alert-messages';
import PrintIcon from '@mui/icons-material/Print';
import ShopsHelper from 'helpers/ShopsHelper';
import Grayout from 'components/utils/GrayOut/Grayout';
import CustomerService from 'services/CustomerService';
import CustomerReportService from 'services/CustomerReportService';
import RemoveRedEyeIcon from '@mui/icons-material/RemoveRedEye';
import { useTranslation } from 'react-i18next';
import { formatBalanceWithCr } from 'helpers/NumberFormatHelper';

function CustomersOutstandingReport() {
  const { t: tCommon } = useTranslation("common");
  const { t: tReport } = useTranslation("report");
  const { shop } = useContext(ShopContext);
  const { postAlertMessage } = useContext(AlertMessagesContext);
  const history = useHistory();

  const [loadingIndicator, setLoadingIndicator] = useState(false);
  const [customerGroups, setCustomerGroups] = useState([]);
  const [filterGroups, setFilterGroups] = useState([]);
  const [sorting, setSorting] = useState('default');
  const [prevFilterData, setPrevFilterData] = useState({
    filterGroups,
    sorting,
  });
  const { reportToPrint, handlePrint: handlePrintReportHook } = useReportPrint(shop, tCommon, tReport, DateTimeHelpers);
  const [items, setItems] = useState([]);
  const [isShowGenerateButton, setIsShowGenerateButton] = useState(null);

  const CSV_COLUMNS = {
    name: tCommon("Customer"),
    mobile: tCommon("Mobile"),
    location: tCommon("Location"),
    credit_limit: tCommon("Credit Limit"),
    balance: {
      title: tCommon("Balance"),
      getValue: (datum) => {
        return ShopsHelper.getAmountFormatted(shop, datum?.balance || 0);
      },
    },
  };

  const isAnyChangeOnReportsFilters = useCallback(() => {
    return sorting !== prevFilterData.sorting || filterGroups !== prevFilterData.filterGroups;
  }, [sorting, filterGroups, prevFilterData.sorting, prevFilterData.filterGroups]);

  useEffect(() => {
    getCustomerGroups();
    generateSummeryReport();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (isShowGenerateButton === null) return;
    setIsShowGenerateButton(isAnyChangeOnReportsFilters());
  }, [isAnyChangeOnReportsFilters, isShowGenerateButton]);

  const handlePrintReport = () => {
    handlePrintReportHook(
      tReport('Customers Outstanding Report'),
      null,
      null,
      headerData,
      items,
      {
        [tCommon('Total Balance')]: `${shop?.currency || ''}  ${summary.totalBalance * -1}`,
      }
    );
  };

  const generateSummeryReport = () => {
    loadSummaryReports({
      balancingSording: sorting,
      group_ids: filterGroups,
    });

    setIsShowGenerateButton(false);
    setPrevFilterData({
      sorting,
      filterGroups,
    });
  };

  const getCustomerGroups = async () => {
    const res = await CustomerService.getCustomerGroups();
    setCustomerGroups(res);
  };

  const toCsvBtnPressed = async () => {
    const csv = CsvHelper.getString(items, CSV_COLUMNS);
    const blob = new Blob([csv], {
      type: 'text/csv',
    });
    const dateTime = DateTimeHelpers.getDate(new Date());
    saveAs(blob, `CUSTOMER-OUTSTANDING-${dateTime}.csv`);

    postAlertMessage({
      text: tReport("Exported to excel successfully"),
      type: 'success',
    });
  };

  const loadSummaryReports = async ({ balanceSorting = sorting, group_ids = filterGroups } = {}) => {
    setLoadingIndicator(true);
    try {
      const data = {
        balance_sorting: balanceSorting || null,
        group_ids: group_ids.length ? group_ids : null,
      };
      const resp = await CustomerReportService.CustomerOutstandingReport(data);
      setItems(resp);
    } catch (error) {
      postAlertMessage({ text: error.message, type: 'failed' });
    }
    setLoadingIndicator(false);
  };

  const _getReportSummary = (items) => {
    let totalBalance = 0;
    items.forEach((item) => {
      totalBalance += item.balance;
    });

    return {
      totalBalance: ShopsHelper.getAmountFormatted(shop, totalBalance),
    };
  };

  const redirectToCustomerrDetails = async (data) => {
    history.push({
      pathname: `${ROUTES.CUSTOMERS}/${data.id}`,
    });
  };


  const headerData = [
    {
      label: tCommon("ID"),
      id: "id",
      type: "text",
    },
    {
      label: tCommon("Name"),
      id: "name",
      type: "text",
    },
    {
      label: tCommon("Mobile"),
      id: "mobile",
      type: "text",
    },
    {
      label: tCommon("Location"),
      id: "location",
      type: "text",
    },
    {
      label: tCommon("Group"),
      id: "group_id",
      type: "callback",
      viewRender: (item) => {
        return customerGroups.find((group) => group.id === item.group_id)?.name || tCommon("N/A");
      },
    },
    {
      label: tCommon("Credit Limit"),
      id: "credit_limit",
      type: "callback",
      viewRender: (item) => {
        return formatBalanceWithCr(ShopsHelper.getAmountFormatted(shop, item.credit_limit));
      },
    },
    {
      label: tCommon("Balance"),
      id: "balance",
      type: "callback",
      viewRender: (item) => {
        const balance = ShopsHelper.getAmountFormatted(shop, item.balance);
        return (
          <Typography variant="body2" style={{ color: "red" }} gutterBottom>
            {formatBalanceWithCr(balance)}
          </Typography>
        );
      },
    },
    {
      label: tCommon("Actions"),
      id: "actions",
      cssClass: "no-print",
      cssClassHeader: "no-print",
      type: "callback",
      viewRender: (item) => {
        return (
          <Grid className={styles.btnGroup}>
            <Tooltip title={tCommon("View Details")} onClick={() => redirectToCustomerrDetails(item)}>
              <RemoveRedEyeIcon className={styles.iconButton} />
            </Tooltip>
          </Grid>
        );
      },
    },
  ];

  const summary = _getReportSummary(items);

  return (
    <div className={styles.contentWrapper}>

      <div className={styles.titleSec}>
        <span className={styles.title}>
          {tCommon("Reports")}
          <span className={styles.menuTitle}>{tCommon("Generation")}</span>
        </span>

        <div style={{ justifyContent: 'flex-end', marginRight: '10px' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon("Print Report")}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={handlePrintReport}
          >
            <PrintIcon className={styles.actionBtnIcon} />
            {tCommon("Print")}
          </Button>
        </div>

        <div style={{ justifyContent: 'flex-end' }}>
          <div style={{ paddingBottom: '4px' }}>
            <label className={styles.label}>{tCommon("Export As")}</label>
          </div>
          <Button
            variant="contained"
            color="primary"
            className={styles.actionBtn}
            style={{ backgroundColor: '#00a65a' }}
            onClick={toCsvBtnPressed}
          >
            <ImportExportIcon className={styles.actionBtnIcon} />
            {tCommon('CSV')}
          </Button>
        </div>
      </div>

      <div className={styles.changeable}>
        <div className={styles.filterSec}>
          <div className={styles.headTitle}>
            <h2 className={styles.subTitle}>{tReport("Customers Outstanding Report")}</h2>
          </div>
        </div>

        <div className={styles.actionButtons}>
          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon("Customer Groups")}</label>
            </div>
            <div>
              <FormControl>
                <Select
                  labelId="demo-multiple-checkbox-label"
                  id="demo-multiple-checkbox"
                  multiple
                  displayEmpty
                  value={filterGroups}
                  onChange={(e) => setFilterGroups(e.target.value)}
                  input={<OutlinedInput />}
                  style={{ width: 135, height: 40 }}
                  renderValue={(filterGroups) => {
                    if (filterGroups.length === 0) return <span>{tCommon("All")}</span>;
                    const selectedNames = filterGroups.map((id) =>
                      customerGroups.find((cg) => cg.id === id).name
                    );
                    return selectedNames.join(', ');
                  }}
                  MenuProps={SelectFilterStyle}
                >
                  {customerGroups.map((group) => (
                    <MenuItem key={group.id} value={group.id}>
                      <Checkbox checked={filterGroups.includes(group.id)} color="primary" />
                      <ListItemText primary={group.name} />
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </div>
          </div>

          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon("Sort")}</label>
            </div>
            <div>
              <TextField
                select
                name="billNo"
                size="small"
                variant="outlined"
                color="primary"
                style={{ width: 200 }}
                value={sorting}
                onChange={(event) => setSorting(event.target.value)}
                displayEmpty
              >
                <MenuItem value={'default'}>{tCommon("Ascending by order")}</MenuItem>
                <MenuItem value={'DESC'}>{tCommon("Descending by balance")}</MenuItem>
                <MenuItem value={'ASC'}>{tCommon("Ascending by balance")}</MenuItem>
              </TextField>
            </div>
          </div>

          <div className={styles.filterDiv}>
            <div style={{ paddingBottom: '4px' }}>
              <label className={styles.label}>{tCommon("Generate Report")}</label>
            </div>
            <div>
              <Button
                variant="contained"
                color="primary"
                className={styles.actionBtn}
                style={{ backgroundColor: '#00a65a' }}
                onClick={generateSummeryReport}
                disabled={isShowGenerateButton === false || loadingIndicator}
              >
                {loadingIndicator ? (
                  <CircularProgress size={20} color="inherit" style={{ marginRight: '10px' }} />
                ) : (
                  <SyncIcon className={styles.actionBtnIcon} />
                )}
                {tCommon("Generate")}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {isShowGenerateButton === null ? (
        <Info
          title={tReport("Set filters and click generate")}
          content={tReport("Report filters info")}
        />
      ) : items && items.length ? (
        <div className={styles.tableContainer}>
          <Grayout open={isShowGenerateButton} />
          <DataTable
            columns={headerData}
            rows={items}
            rowKey="id"
            summary={{
              name: tCommon("Total"),
              balance: `${shop?.currency || ''}  ${summary.totalBalance * -1}`,
            }}
          />
        </div>
      ) : (
        !loadingIndicator && (
          <Info
            severity="warning"
            title={tReport("No Customer Found")}
            content={tReport("There are no Customers found under this Customer Group")}
          />
        )
      )}
      {reportToPrint &&
        createPortal(<PrintDocument type={PRINT_TYPES.REPORT} data={reportToPrint} shop={shop} />, document.body)}
    </div>
  );
}

export default withConsoleBase(CustomersOutstandingReport);
